import {
  AnalyticsEventArgs,
  AnalyticsEventName,
  AnalyticsProperties,
} from './events';

export type AnalyticsSurface =
  | 'manager-dashboard'
  | 'manager-app'
  | 'resident-app';

export type AnalyticsUserType =
  | 'client_admin'
  | 'manager'
  | 'concierge'
  | 'community_manager'
  | 'rmc_director'
  | 'staff'
  | 'resident'
  | 'leaseholder'
  | 'applicant';

export type AnalyticsPersonProperties = {
  email: string | null;
  name: string | null;
  user_type: AnalyticsUserType | null;
  is_internal: boolean;
  is_demo: boolean | null;
};

export type AnalyticsGroupType = 'client' | 'community';

export type AnalyticsGroup = {
  key: string;
  name: string;
  clientType?: string | null;
};

export type AnalyticsGroups = Partial<
  Record<AnalyticsGroupType, AnalyticsGroup>
>;

export type AnalyticsIdentity = {
  distinctId: string;
  person: AnalyticsPersonProperties;
  groups: AnalyticsGroups;
};

export type AnalyticsClient = {
  capture: (event: string, properties?: AnalyticsProperties) => unknown;
  identify: (distinctId: string, properties?: AnalyticsProperties) => unknown;
  group: (
    groupType: string,
    groupKey: string,
    groupProperties?: AnalyticsProperties,
  ) => unknown;
  setPersonProperties: (properties: AnalyticsProperties) => unknown;
  resetGroups: () => unknown;
  getDistinctId: () => string;
};

export type AnalyticsStorage = {
  getItem: (key: string) => string | null | Promise<string | null>;
  setItem: (key: string, value: string) => unknown;
  removeItem: (key: string) => unknown;
};

const IDENTITY_SYNC_KEY = 'ark_analytics_identity';
const GROUPS_SYNC_KEY = 'ark_analytics_groups';

const INTERNAL_EMAIL_DOMAINS = ['projectark.co.uk', 'ark.rent'];

export const isInternalEmail = (email: string) =>
  INTERNAL_EMAIL_DOMAINS.some(domain =>
    email.toLowerCase().endsWith(`@${domain}`),
  );

export const trackEvent = <Name extends AnalyticsEventName>(
  client: AnalyticsClient,
  event: Name,
  ...[properties]: AnalyticsEventArgs<Name>
) => {
  client.capture(event, properties);
};

const groupProperties = (group: AnalyticsGroup): AnalyticsProperties =>
  group.clientType
    ? {name: group.name, client_type: group.clientType}
    : {name: group.name};

let syncGeneration = 0;

const runIfChanged = async (
  storage: AnalyticsStorage,
  key: string,
  fingerprint: string,
  generation: number,
  action: () => void,
) => {
  const stored = await storage.getItem(key);
  if (stored === fingerprint || generation !== syncGeneration) {
    return;
  }
  action();
  await storage.setItem(key, fingerprint);
  if (generation !== syncGeneration) {
    await storage.removeItem(key);
  }
};

const clearSyncMarkers = async (storage: AnalyticsStorage) => {
  await storage.removeItem(IDENTITY_SYNC_KEY);
  await storage.removeItem(GROUPS_SYNC_KEY);
};

export const clearAnalyticsSync = async (storage: AnalyticsStorage) => {
  syncGeneration += 1;
  await clearSyncMarkers(storage);
};

const sendGroups = (client: AnalyticsClient, groups: AnalyticsGroups) => {
  if (!groups.client || !groups.community) {
    client.resetGroups();
  }
  if (groups.client) {
    client.group('client', groups.client.key, groupProperties(groups.client));
  }
  client.setPersonProperties({client_name: groups.client?.name ?? null});
  if (groups.community) {
    client.group(
      'community',
      groups.community.key,
      groupProperties(groups.community),
    );
  }
};

const syncGroups = (
  client: AnalyticsClient,
  storage: AnalyticsStorage,
  groups: AnalyticsGroups,
  generation: number,
) =>
  runIfChanged(
    storage,
    GROUPS_SYNC_KEY,
    JSON.stringify(groups),
    generation,
    () => sendGroups(client, groups),
  );

export const setAnalyticsGroups = (
  client: AnalyticsClient,
  storage: AnalyticsStorage,
  groups: AnalyticsGroups,
) => syncGroups(client, storage, groups, syncGeneration);

const withoutEmptyValues = (
  person: AnalyticsPersonProperties,
): AnalyticsProperties =>
  Object.fromEntries(
    Object.entries(person).filter(([, value]) => value !== null),
  );

export const identifyAnalyticsUser = async (
  client: AnalyticsClient,
  storage: AnalyticsStorage,
  identity: AnalyticsIdentity,
) => {
  const generation = syncGeneration;
  const currentDistinctId = client.getDistinctId();
  if (currentDistinctId && currentDistinctId !== identity.distinctId) {
    await clearSyncMarkers(storage);
  }
  const person = withoutEmptyValues(identity.person);
  const fingerprint = JSON.stringify({distinctId: identity.distinctId, person});
  await runIfChanged(storage, IDENTITY_SYNC_KEY, fingerprint, generation, () =>
    client.identify(identity.distinctId, person),
  );
  if (identity.groups.client || identity.groups.community) {
    await syncGroups(client, storage, identity.groups, generation);
  }
};
